import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/**
 * Valor de uma unidade extraída que o consultor corrigiu: o que a extração
 * trouxe, riscado, e o valor novo. Editar de novo troca só o valor novo — o
 * riscado continua sendo o da extração, que nunca é alterada.
 *
 * `inline` (lista e card) põe os dois lado a lado; `stacked` (painel de
 * detalhes) põe o novo embaixo. `<del>` carrega a semântica de "valor
 * substituído"; o prefixo `sr-only` diz em voz alta o que o risco diz na tela.
 */
export function CorrectedValue({
  extracted,
  current,
  emptyLabel,
  layout = "inline",
  className,
}: {
  extracted: string | null;
  current: ReactNode;
  /** Texto do extraído quando a extração não trouxe valor nenhum. */
  emptyLabel: string;
  layout?: "inline" | "stacked";
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <span
      data-testid="corrected-value"
      className={cn(
        layout === "inline" ? "inline-flex items-baseline gap-1.5" : "flex flex-col gap-0.5",
        className,
      )}
    >
      <del
        data-testid="extracted-value"
        title={t("Extracted value")}
        className="font-normal text-[#7b8799] decoration-[#7b8799]"
      >
        <span className="sr-only">{t("Extracted value")}: </span>
        {extracted ?? emptyLabel}
      </del>
      <span>{current}</span>
    </span>
  );
}
